import { API_ENDPOINTS } from './endpoints';
import { REQ_METHODS } from './methods';
import { CONTENT_TYPES } from './contentTypes';
import { getUserPool } from './cognito';
import pubSub from '../utils/pubsub';
import { disableMockGlobal } from '../utils/constants/variables';

const activeControllers = [];

pubSub.subscribe((payload) => {
  if (payload?.event === 'logout') {
    activeControllers.forEach(({ controller }) => {
      controller.abort();
    });
    activeControllers.length = 0;
  }
});

const _cancelRequest = (path = '') => {
  if (typeof path !== 'string') return;

  for (let i = activeControllers.length - 1; i >= 0; i--) {
    if (activeControllers[i].path === path) {
      activeControllers[i].controller.abort();
      activeControllers.splice(i, 1);
    }
  }
};

pubSub.subscribe((payload) => {
  if (payload?.event === 'cancel-request') {
    if (Array.isArray(payload.endpoints)) {
      payload.endpoints.forEach(_cancelRequest);
    } else {
      _cancelRequest(payload.endpoint);
    }
  }
});

/**
 * Cancels an API request (or multiple) by publishing a `cancel-request` event.
 *
 * @param {Object|Object[]} endpoint - A single endpoint object or an array of endpoint objects.
 * Each object must have a `path` property of type string.
 *
 * Example single:
 * { path: '/api/search' }
 *
 * Example multiple:
 * [{ path: '/api/search' }, { path: '/api/poll' }]
 */
export const cancelRequest = (endpoint) => {
  if (!endpoint) return;

  if (Array.isArray(endpoint)) {
    const validPaths = endpoint
      .filter((item) => item && typeof item.path === 'string')
      .map((item) => item.path);

    if (validPaths.length > 0) {
      pubSub.publish({
        event: 'cancel-request',
        endpoints: validPaths
      });
    }
  } else if (typeof endpoint === 'object' && typeof endpoint.path === 'string') {
    pubSub.publish({
      event: 'cancel-request',
      endpoint: endpoint.path
    });
  }
};

/**
 * Resolves the current Cognito session via the SDK's callback API.
 * Returns null if there is no signed-in user or the session can't be retrieved.
 * Kept private to api.js so token retrieval stays adjacent to the request that uses it.
 */
const _getCognitoSession = () =>
  new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err, session) => {
      if (err) {
        resolve(null);
        return;
      }
      resolve(session || null);
    });
  });

const _validateEndpoint = (endpoint) => {
  if (!Object.values(API_ENDPOINTS).find((e) => endpoint.path.startsWith(e.path))) {
    throw new Error(`${JSON.stringify(endpoint)} is invalid request endpoint!`);
  }
  if (!Object.values(REQ_METHODS).includes(endpoint.method)) {
    throw new Error(`${endpoint.method} is invalid request method!`);
  }
};

// Backstop for endpoints that opt in via `withCognitoToken` / `withCognitoAccessToken`.
// Existing callers (store.makeRequest, agentSessions) prefill Authorization themselves,
// so caller-provided headers always win.
const _injectCognitoHeaders = async (endpoint, headers) => {
  const needsIdToken = endpoint.withCognitoToken && !headers.Authorization;
  const needsAccessToken = endpoint.withCognitoAccessToken && !headers['x-cognito-access-token'];
  if (!needsIdToken && !needsAccessToken) return headers;

  const session = await _getCognitoSession();
  if (!session) return headers;

  const next = { ...headers };
  if (needsIdToken) {
    const idToken = session.getIdToken?.().getJwtToken?.() ?? session.idToken?.jwtToken;
    if (idToken) next.Authorization = idToken;
  }
  if (needsAccessToken) {
    const accessToken =
      session.getAccessToken?.().getJwtToken?.() ?? session.accessToken?.jwtToken;
    if (accessToken) next['x-cognito-access-token'] = accessToken;
  }
  return next;
};

const _buildRequestUrl = (endpoint, params) => {
  const base = `${import.meta.env.VITE_API_BASEURL}${endpoint.path}`;
  if (!Object.keys(params).length) return base;
  const query = Object.entries(params)
    .map(([param, value]) => `${param}=${value}`)
    .join('&');
  return `${base}?${query}`;
};

const _buildMockPromise = (endpoint, body) => {
  if (disableMockGlobal) return null;
  if (typeof endpoint.mockResponseData === 'function') {
    return new Promise((resolve) => {
      setTimeout(() => {
        const data = endpoint.mockResponseData(body);
        if (typeof endpoint.dataMapper === 'function') {
          endpoint.dataMapper(data);
        }
        resolve({ success: true, data });
      }, 200);
    });
  }
  if (endpoint.error) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: false, errors: { errorMessages: [endpoint.error] } });
      }, 200);
    });
  }
  return null;
};

const _buildFetchPromise = (endpoint, req, params, responseInfo) => {
  const isContentJSON = req?.headers?.['Content-Type'] === CONTENT_TYPES.APPLICATION_JSON;
  return fetch(_buildRequestUrl(endpoint, params), req)
    .then((res) => {
      responseInfo.done = true;
      if (isContentJSON) {
        // `httpStatus` (not `status`) — the BE reveal/bulk-reveal/download-preview
        // endpoints return flat bodies whose own `status` field carries semantic
        // meaning ('success'|'not_found'|'pending'). Using `status` here would
        // clobber that field. The store/onError contract reads response.httpStatus.
        const httpStatus = res.status;
        return res.json().then((body) => ({ ...body, httpStatus }));
      }
      return res;
    })
    .then((res) => {
      if (isContentJSON && res.success && typeof endpoint.dataMapper === 'function') {
        endpoint.dataMapper(res.data);
      }
      return res;
    });
};

const _buildTimeoutPromise = (endpoint, controller, responseInfo) =>
  new Promise((resolve) => {
    setTimeout(
      () => {
        resolve({
          success: false,
          errorMessage: `Connection timed out (${endpoint.method} '${endpoint.path}')`
        });
        if (!responseInfo.done) {
          controller.abort();
        }
      },
      +(import.meta.env.VITE_API_TIMEOUT_MS || 20_000)
    );
  });

export const request = async ({ endpoint, body = {}, headers = {}, params = {} }) => {
  _validateEndpoint(endpoint);

  const finalHeaders = await _injectCognitoHeaders(endpoint, headers);

  const controller = new AbortController();
  const isExcluded =
    endpoint.path === API_ENDPOINTS.GET_PUBLIC_OPTIONS.path &&
    endpoint.method === API_ENDPOINTS.GET_PUBLIC_OPTIONS.method;

  if (!isExcluded) {
    activeControllers.push({ controller, path: endpoint.path });
  }

  const req = {
    method: endpoint.method,
    headers: {
      'Content-Type': endpoint.contentType || CONTENT_TYPES.APPLICATION_JSON,
      ...finalHeaders
    },
    signal: controller.signal
  };

  const { POST, PUT, PATCH, DELETE } = REQ_METHODS;
  if ([POST, PUT, PATCH, DELETE].includes(endpoint.method)) {
    req.body = JSON.stringify(body);
  }

  const responseInfo = { done: false };
  const requestPromise =
    _buildMockPromise(endpoint, body) ?? _buildFetchPromise(endpoint, req, params, responseInfo);
  const timeoutPromise = _buildTimeoutPromise(endpoint, controller, responseInfo);

  const response = await Promise.race([requestPromise, timeoutPromise]);

  if (!isExcluded) {
    const index = activeControllers.findIndex((c) => c.controller === controller);
    if (index !== -1) {
      activeControllers.splice(index, 1);
    }
  }

  return response;
};
